// Pure UI for one chat pane.
//
// State + Gemini wiring live in useChatAgent; this component renders:
//   ┌─────────────────────────────────────────────────┐
//   │ [<id>] <shortLabel> <emoji> <label>             │  ← header
//   ├─────────────────────────────────────────────────┤
//   │ <messages, oldest top, newest bottom>           │  ← scroll area
//   │   system  : gray italic                         │
//   │   user    : primary, right-aligned              │
//   │   agent   : gray-100, left-aligned              │
//   │   tool    : secondary amber                     │
//   ├─────────────────────────────────────────────────┤
//   │ /pill /pill /pill ...                           │  ← quick palette
//   │ > _input_                                       │  ← prompt + input
//   └─────────────────────────────────────────────────┘
//
// Click anywhere → onActivate(). Dim opacity when !isActive.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { AgentConfig, ChatMessage, SlashCommandResult } from './AgentConfig';
import { useChatAgent } from './useChatAgent';
import styles from '../Terminal.module.css';

// ---- Accent colors ---------------------------------------------------------
//
// AgentConfig.accentColor is one of: 'primary' | 'secondary' | 'pink'.
// We don't use full Tailwind class strings (purge would strip them); instead
// resolve to hex values applied as inline style.
const ACCENT: Record<NonNullable<AgentConfig['accentColor']>, string> = {
  primary:   '#3ddc84',
  secondary: '#fbbf24',
  pink:      '#f472b6',
};

function accentHex(c?: AgentConfig['accentColor']): string {
  return ACCENT[c ?? 'primary'];
}

// ---- Time formatting -------------------------------------------------------

function fmtTime(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// ---- Props -----------------------------------------------------------------

export type ChatPaneAction = NonNullable<SlashCommandResult['action']>;

interface ChatPaneProps {
  config: AgentConfig;
  isActive: boolean;
  onActivate: () => void;
  onAction?: (action: ChatPaneAction) => void;
}

// ---- Message row -----------------------------------------------------------

interface RowProps {
  message: ChatMessage;
  accent: string;
}

const MessageRow: React.FC<RowProps> = ({ message, accent }) => {
  const time = fmtTime(message.timestamp);

  if (message.role === 'system') {
    return (
      <div className="text-gray-500 italic leading-relaxed">
        <span className="text-[11px] text-gray-600 mr-2">{time}</span>
        {message.text}
      </div>
    );
  }

  if (message.role === 'tool') {
    return (
      <div className="text-secondary-300 leading-relaxed">
        <span className="text-[11px] text-gray-600 mr-2">{time}</span>
        <span style={{ whiteSpace: 'pre-wrap' }}>{message.text}</span>
      </div>
    );
  }

  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div
          className="max-w-[85%] text-right leading-relaxed"
          style={{ color: accent }}
        >
          <div className="text-[11px] text-gray-600">
            you <span className="ml-1">{time}</span>
          </div>
          <span style={{ whiteSpace: 'pre-wrap' }}>{message.text}</span>
        </div>
      </div>
    );
  }

  // agent
  return (
    <div className="flex justify-start">
      <div className="max-w-[90%] text-gray-100 leading-relaxed">
        <div className="text-[11px] text-gray-600">
          agent <span className="ml-1">{time}</span>
        </div>
        <span style={{ whiteSpace: 'pre-wrap' }}>{message.text}</span>
        {message.streaming && (
          <span className={styles.streamingDot} aria-label="streaming" />
        )}
      </div>
    </div>
  );
};

// ---- Pane ------------------------------------------------------------------

const ChatPane: React.FC<ChatPaneProps> = ({ config, isActive, onActivate, onAction }) => {
  const {
    messages,
    input,
    setInput,
    send,
    isStreaming,
    runSlash: _runSlash,  // available for future direct-invocation; pill clicks currently just fill input
    setOnAction,
  } = useChatAgent(config);

  // Wire side-effect handler.
  useEffect(() => {
    setOnAction(onAction ?? null);
    return () => setOnAction(null);
  }, [onAction, setOnAction]);

  // ---- refs & autoscroll --------------------------------------------------
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  // When this pane becomes active, focus the input so typing "just works".
  useEffect(() => {
    if (isActive) inputRef.current?.focus();
  }, [isActive]);

  // ---- one-deep history recall (Up arrow) ---------------------------------
  const lastUserRef = useRef<string>('');
  useEffect(() => {
    // Track the most recent user message any time it changes.
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        lastUserRef.current = messages[i].text;
        break;
      }
    }
  }, [messages]);

  // ---- slash type-ahead ---------------------------------------------------
  const allCommands = useMemo(() => {
    const list = config.slashCommands.map(c => ({
      name: c.name,
      description: c.description,
      example: c.example,
    }));
    // Synthetic /help — useChatAgent handles execution.
    list.unshift({ name: '/help', description: 'List available commands', example: '/help' });
    return list;
  }, [config.slashCommands]);

  const slashSuggestions = useMemo(() => {
    if (!input.startsWith('/')) return [];
    const q = input.toLowerCase();
    return allCommands.filter(c => c.name.toLowerCase().startsWith(q)).slice(0, 6);
  }, [input, allCommands]);

  const [showSuggestions, setShowSuggestions] = useState(false);
  useEffect(() => {
    setShowSuggestions(slashSuggestions.length > 0 && input.startsWith('/'));
  }, [slashSuggestions.length, input]);

  // ---- key handler --------------------------------------------------------
  // Tab cycles through slash suggestions instead of escaping the pane.
  // Pressed once -> first match; pressed again -> next; Shift+Tab -> previous.
  const tabIndexRef = useRef<number>(-1);

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      send();
      setShowSuggestions(false);
      tabIndexRef.current = -1;
      return;
    }
    // Tab autocomplete — only when typing a slash command.
    if (e.key === 'Tab' && input.startsWith('/') && slashSuggestions.length > 0) {
      e.preventDefault();
      const step = e.shiftKey ? -1 : 1;
      const len = slashSuggestions.length;
      tabIndexRef.current = (tabIndexRef.current + step + len) % len;
      setInput(slashSuggestions[tabIndexRef.current].name);
      return;
    }
    // Any non-Tab key resets the cycle so the next Tab starts at the top.
    if (e.key !== 'Shift') {
      tabIndexRef.current = -1;
    }
    if (e.key === 'ArrowUp' && input === '' && lastUserRef.current) {
      e.preventDefault();
      setInput(lastUserRef.current);
      return;
    }
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  }

  // ---- styles -------------------------------------------------------------
  const accent = accentHex(config.accentColor);

  // Pane chrome: 2px border + soft inner glow when active; gray when not.
  const paneStyle: React.CSSProperties = {
    fontFamily: "'JetBrains Mono', ui-monospace, monospace",
    fontSize: '14px',
    border: isActive ? '2px solid #3ddc84' : '2px solid #1f2937',
    boxShadow: isActive ? 'inset 0 0 20px rgba(61, 220, 132, 0.05)' : 'none',
    opacity: isActive ? 1 : 0.6,
    transition: 'opacity 120ms ease-out, border-color 120ms ease-out',
  };

  return (
    <div
      className="h-full w-full flex flex-col bg-surface-dark relative"
      style={paneStyle}
      onMouseDown={onActivate}
    >
      {/* Header */}
      <div
        className="px-3 py-1 flex items-center gap-2 border-b border-gray-800 flex-shrink-0"
        style={{ color: accent }}
      >
        <span className="text-[12px] text-gray-500">[{config.id}]</span>
        <span className="text-[12px] text-gray-500">{config.shortLabel}</span>
        <span aria-hidden="true">{config.emoji}</span>
        <span className="font-semibold">{config.label}</span>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className={`flex-1 min-h-0 overflow-y-auto px-3 py-2 space-y-2 ${styles.scrollArea}`}
      >
        {messages.map(m => (
          <MessageRow key={m.id} message={m} accent={accent} />
        ))}
      </div>

      {/* Slash command quick palette */}
      {allCommands.length > 0 && (
        <div className="px-3 py-1 flex flex-wrap gap-1 border-t border-gray-800 flex-shrink-0">
          {allCommands.slice(0, 8).map(c => (
            <button
              key={c.name}
              type="button"
              onMouseDown={(e) => {
                // Don't lose focus to the parent activation handler — but DO
                // call onActivate so this pane lights up.
                e.stopPropagation();
                onActivate();
              }}
              onClick={() => {
                // Insert the command into the input rather than firing
                // immediately — the user can add args before hitting Enter.
                setInput(c.name + (c.example && c.example.includes(' ') ? ' ' : ''));
                inputRef.current?.focus();
              }}
              title={c.description}
              className="text-[11px] px-1.5 py-0.5 rounded border border-gray-800 hover:border-primary-400 text-gray-400 hover:text-primary-400 transition-colors"
            >
              {c.name}
            </button>
          ))}
        </div>
      )}

      {/* Suggestion dropdown */}
      {showSuggestions && (
        <div
          className="absolute left-3 right-3 bottom-12 z-20 rounded border border-gray-700 bg-[#0a0f0d] shadow-lg"
          style={{ fontSize: '12px' }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {slashSuggestions.map(s => (
            <button
              key={s.name}
              type="button"
              onClick={() => {
                setInput(s.name + ' ');
                setShowSuggestions(false);
                inputRef.current?.focus();
              }}
              className="w-full text-left px-2 py-1 flex items-baseline gap-2 hover:bg-primary-400/10 hover:text-primary-400 text-gray-300"
            >
              <span className="font-semibold">{s.name}</span>
              <span className="text-[11px] text-gray-500 truncate">{s.description}</span>
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div
        className="flex items-center gap-2 px-3 py-1.5 border-t border-gray-800 flex-shrink-0"
        onMouseDown={(e) => {
          // Activating via the input row shouldn't steal focus from the input.
          e.stopPropagation();
          onActivate();
        }}
      >
        <span style={{ color: accent }}>&gt;</span>
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={onActivate}
          disabled={isStreaming}
          placeholder={isStreaming ? 'streaming...' : 'type a message or /command'}
          className="flex-1 bg-transparent text-gray-100 placeholder:text-gray-600 focus:outline-none disabled:opacity-50"
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: '14px',
            caretColor: accent,
          }}
        />
      </div>
    </div>
  );
};

export default ChatPane;
